import axios from 'axios';
import apiClient from './apiClient';
import { extractDataArray } from './utils/apiResponseUtils';
import { handleApiError as handleError } from './utils/apiErrorUtils';

// Extractor interfaces aligned with API specification
export interface Extractor {
  id: number;
  uuid?: string;
  name?: string;
  description?: string;
  extractor_type?: string;
  // Support both naming conventions
  platform_id?: number;
  platform?: number;
  platform_name?: string;
  job_type_id?: number;
  job_type?: number;
  job_type_name?: string;
  job_type_code?: string;
  config?: any;
  extraction_plan?: any;
  version?: number;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExtractorListResponse {
  count: number;
  results: Extractor[];
}

export interface ExtractorCreate {
  name: string;
  description?: string;
  extractor_type: string;
  platform_id?: number;
  config?: any;
  is_active?: boolean;
}

export interface ExtractorListParams {
  platform_id?: number;
  extractor_type?: string;
  is_active?: boolean;
}

class ExtractorService {
  async getAllExtractors(params?: ExtractorListParams): Promise<Extractor[]> {
    try {
      const response = await apiClient.get<any>('/api/extractors/', { params });
      
      
      return extractDataArray<Extractor>(response.data, ['extractors', 'results', 'data']);
    } catch (error) {
      console.error('Failed to fetch extractors:', error);
      // Return empty array instead of throwing to prevent UI breaks
      return [];
    }
  }

  /**
   * Gets extractor configurations for YAML generation
   */
  async getExtractorConfigs(): Promise<Extractor[]> {
    try {
      const response = await apiClient.get<any>('/extractors/');
      
      
      return extractDataArray<Extractor>(response.data, ['extractors', 'results', 'data']);
    } catch (error) {
      console.error('Failed to fetch extractor configs:', error);
      return [];
    }
  }

  async getExtractor(id: number): Promise<Extractor | null> {
    try {
      const response = await apiClient.get<Extractor>(`/api/extractors/${id}/`);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  }

  async createExtractor(data: ExtractorCreate): Promise<Extractor> {
    try {
      const response = await apiClient.post<Extractor>('/api/extractors/', data);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  }

  async updateExtractor(id: number, data: Partial<ExtractorCreate>): Promise<Extractor> {
    try {
      const response = await apiClient.put<Extractor>(`/api/extractors/${id}/`, data);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  }

  async deleteExtractor(id: number): Promise<void> {
    try {
      await apiClient.delete(`/api/extractors/${id}/`);
    } catch (error) {
      throw handleError(error);
    }
  }

}

export const extractorService = new ExtractorService();